import { useEffect, useState } from "react"
import { useSearchParams } from "react-router-dom"

import {
  getActiveTabFieldName,
  getIdsDrawer,
  getOpenDrawersFromQueryString
} from "@/organisms/Drawers/hooks/useDrawer"
import { useKai } from "../KaiChat"
import { AgentAiDiscoveryDrawer } from "./components/AgentAiDiscoveryDrawer"
import { AiPolicyViolationsDrawer } from "./components/AiPolicyViolationsDrawer"
import { ApplicationDrawer } from "./components/ApplicationDrawer"
import { CodeRepositoryDrawer } from "./components/CodeRepositoryDrawer"
import { ImageDrawer } from "./components/ImageDrawer"
import { PackageDrawer } from "./components/PackageDrawer"
import { QuickWinsDrawer } from "./components/QuickWinsDrawer"
import { TriageDrawer } from "./components/TriageDrawer"
import { VirtualMachineDrawer } from "./components/VirtualMachineDrawer"
import { WorkflowAiDiscoveryDrawer } from "./components/WorkflowAiDiscoveryDrawer"
import * as IDS from "./const"

export const getDrawerType = (type: string) => type[0]

interface DrawerComponentProps {
  id: string
  previousId?: string
  open: boolean
  requestCloseDrawer?: (id: string) => void
  widthAdditive: number
}

// The drawer each type of id in the url opens
const DRAWER_COMPONENTS: Partial<
  Record<string, React.FC<DrawerComponentProps>>
> = {
  [IDS.CODE_REPOSITORY_DRAWER_ID]: CodeRepositoryDrawer,
  [IDS.IMAGE_DRAWER_ID]: ImageDrawer,
  [IDS.APPLICATION_DRAWER_ID]: ApplicationDrawer,
  [IDS.TRIAGE_DRAWER_ID]: TriageDrawer,
  [IDS.PACKAGE_DRAWER_ID]: PackageDrawer,
  [IDS.VIRTUAL_MACHINE_DRAWER_ID]: VirtualMachineDrawer,
  [IDS.QUICK_WINS_DRAWER_ID]: QuickWinsDrawer,
  [IDS.AGENT_AI_DISCOVERY_DRAWER_ID]: AgentAiDiscoveryDrawer,
  [IDS.WORKFLOW_AI_DISCOVERY_DRAWER_ID]: WorkflowAiDiscoveryDrawer,
  [IDS.AI_POLICY_VIOLATIONS_DRAWER_ID]: AiPolicyViolationsDrawer
}

const getDrawerComponent = (id: string) => DRAWER_COMPONENTS[getDrawerType(id)]

export const Drawers = () => {
  const [closing, setClosing] = useState<Record<string, boolean>>({})
  const [searchParams, setSearchParams] = useSearchParams()
  const drawers = getIdsDrawer(searchParams)
  const { open } = useKai()
  const count = drawers.length
  const requestClose = (drawerId: string) => {
    setClosing({ ...closing, [drawerId]: true })
  }
  const drawerIdsFromQueryString = getOpenDrawersFromQueryString(searchParams)

  useEffect(() => {
    const index = Object.keys(closing).findIndex((id) => !!closing[id])

    if (index === -1) return

    setClosing((prevState) =>
      Object.keys(prevState).reduce(
        (acc, id) => {
          if (!searchParams.get(id)) delete acc[id]

          return acc
        },
        { ...prevState } as Record<string, boolean>
      )
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drawerIdsFromQueryString])

  useEffect(() => {
    const result = drawers.filter((id) => {
      if (searchParams.get(id)) return true

      searchParams.delete(getActiveTabFieldName(id))
      return false
    })

    if (result.length === drawers.length) return

    if (!result.length) searchParams.delete(IDS.DRAWERS_PARAM_NAME)
    else searchParams.set(IDS.DRAWERS_PARAM_NAME, result.toString())

    setSearchParams(searchParams)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drawers])

  const hasOpenDrawer = drawers.some((id) => !!getDrawerComponent(id))

  return (
    <>
      {drawers.map((id, index) => {
        const DrawerComponent = getDrawerComponent(id)

        if (!DrawerComponent) return null

        const widthAdditive =
          (count - index) * 50 -
          (open && document.body.clientWidth < 1600 ? 300 : 0)
        const props = {
          id: `${id}`,
          previousId: drawers[index - 1],
          open: !closing[id],
          requestCloseDrawer: requestClose,
          widthAdditive
        }

        return <DrawerComponent key={id} {...props} />
      })}
      {hasOpenDrawer && <div className="mask-drawer-ks" />}
    </>
  )
}
